import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

interface UnsavedChangesContextValue {
  isDirty: boolean;
  registerDirty: (id: string, dirty: boolean) => void;
  unregisterDirty: (id: string) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

/**
 * Tracks whether ANY currently-mounted form on the page is dirty. Forms register
 * their own dirty flag via useUnsavedChanges(isDirty) below; a page can have more
 * than one form (e.g. a filter bar + a create form), so this is a count, not a
 * single boolean overwrite — the last form to unmount clean shouldn't clear a
 * dirty flag still held by another.
 */
export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dirtyMap = useRef<Map<string, boolean>>(new Map());
  const [isDirty, setIsDirty] = useState(false);

  const recompute = useCallback(() => {
    let any = false;
    dirtyMap.current.forEach((v) => {
      if (v) any = true;
    });
    setIsDirty(any);
  }, []);

  const registerDirty = useCallback((id: string, dirty: boolean) => {
    dirtyMap.current.set(id, dirty);
    recompute();
  }, [recompute]);

  const unregisterDirty = useCallback((id: string) => {
    dirtyMap.current.delete(id);
    recompute();
  }, [recompute]);

  const value = useMemo(
    () => ({ isDirty, registerDirty, unregisterDirty }),
    [isDirty, registerDirty, unregisterDirty],
  );

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>;
};

/** Read-only view of the aggregate dirty state — used by the language switcher. */
export const useUnsavedChangesState = (): { isDirty: boolean } => {
  const ctx = useContext(UnsavedChangesContext);
  return { isDirty: ctx?.isDirty ?? false };
};

let nextFormId = 0;

/**
 * Forms call this with their own dirty flag (e.g. react-hook-form's
 * formState.isDirty) to register it against the page-level tracker. A no-op if
 * rendered outside UnsavedChangesProvider, so it's safe to add to a form even
 * before the provider is wired up everywhere.
 */
export const useUnsavedChanges = (isDirty: boolean): void => {
  const ctx = useContext(UnsavedChangesContext);
  const idRef = useRef<string>();
  if (!idRef.current) {
    nextFormId += 1;
    idRef.current = `form-${nextFormId}`;
  }

  useEffect(() => {
    if (!ctx) return;
    const id = idRef.current!;
    ctx.registerDirty(id, isDirty);
    return () => ctx.unregisterDirty(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, isDirty]);
};
